import React, { useState, useEffect, useCallback, useRef } from 'react';
import { can } from '../../utils/roleUtils';
import {
  Box, Grid, Card, CardContent, Typography, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, IconButton, Tooltip, useTheme,
  Tabs, Tab, Collapse, Alert, TablePagination, LinearProgress, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowRight as ExpandLessIcon,
  PersonAdd as PersonAddIcon,
  UploadFile as UploadFileIcon,
  DeleteOutline as DeleteIcon,
  ManageAccounts as StatusIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import ApiService from '../../services/api.service';
import tcDocumentTypeService from '../../services/tc-document-type.service';
import FinancialSummaryCard from '../../components/dashboard/FinancialSummaryCard';
import AddClientEmployeeDialog from '../../components/client-employees/AddClientEmployeeDialog';
import UploadClientEmployeeDocumentsDialog from '../../components/client-employees/UploadClientEmployeeDocumentsDialog';
import EmployeeDocuments from '../../components/client-employees/EmployeeDocuments';
import OrgDocumentsTab from '../../components/org-documents/OrgDocumentsTab';
import ClientPortalTab from './ClientPortalTab';
import ClientCardsTab from './ClientCardsTab';
import ChangeEmployeeStatusDialog, { EMPLOYEE_STATUSES } from '../../components/client-employees/ChangeEmployeeStatusDialog';

const EMPLOYEE_STATUS_FILTERS = [
  { value: 'NOT_DEACTIVATED', label: 'Current', statuses: ['ACTIVE', 'ABSCONDED', 'LONG_LEAVE'] },
  { value: 'ALL', label: 'All', statuses: null },
  ...EMPLOYEE_STATUSES.map((s) => ({ value: s.value, label: s.label, statuses: [s.value] }))
];
const statusFilterList = (filter) => EMPLOYEE_STATUS_FILTERS.find((f) => f.value === filter)?.statuses || null;

const InternalOrgDetail = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') === 'documents' ? 'documents' : 'overview');
  const [org, setOrg] = useState(location.state?.org || null);

  // Employee/document tracking is a client-only Thaklis feature. Use the customer's own type when
  // known; for rows created before the type field existed, fall back to the parent TC — a B2B
  // provider's customers default to typing-center (no tracking), everyone else to client.
  const sessionUser = (() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
  })();
  // Stable booleans (not the object) so they can sit in hook dependency arrays.
  const canReadFinance = can(sessionUser, 'finance', 'read');
  const canWriteFinance = can(sessionUser, 'finance', 'write');
  const canDeleteFinance = can(sessionUser, 'finance', 'delete');
  const canReadInvoice = can(sessionUser, 'invoice', 'read');
  const canReadWorkOrder = can(sessionUser, 'work_order', 'read');
  const canWriteOrg = can(sessionUser, 'customer_organization', 'write');
  // Hard-deleting a document version needs the same permission as deleting a client.
  const canDeleteOrg = can(sessionUser, 'customer_organization', 'delete');
  const orgType = org?.type || org?.orgType
    || (sessionUser?.organizationType === 'B2B' ? 'TYPING_CENTER' : 'CLIENT');
  const showEmployeesTab = orgType === 'CLIENT';
  const [invoices, setInvoices] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [summary, setSummary] = useState(null);
  const [walletOk, setWalletOk] = useState(false);
  const [summaryOk, setSummaryOk] = useState(false);
  const [loading, setLoading] = useState(true);

  // Employees tab (client employees + their documents, tracked in Thaklis Main). Loaded lazily.
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeesError, setEmployeesError] = useState(null);
  const [empPage, setEmpPage] = useState(0);
  const [empRowsPerPage, setEmpRowsPerPage] = useState(20);
  const [empTotal, setEmpTotal] = useState(0);
  const [expandedUid, setExpandedUid] = useState(null);
  // Grouped documents by employee uid: { [uid]: { loading, error, groups } }
  const [docsByUid, setDocsByUid] = useState({});
  const [viewingDoc, setViewingDoc] = useState(null);
  const [addEmployeeOpen, setAddEmployeeOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [empStatusFilter, setEmpStatusFilter] = useState('NOT_DEACTIVATED');
  const [employeeNotice, setEmployeeNotice] = useState(null);

  // Success notes close on their own; warnings and errors stay until closed.
  useEffect(() => {
    if (employeeNotice?.severity !== 'success') return undefined;
    const t = setTimeout(() => setEmployeeNotice(null), 4000);
    return () => clearTimeout(t);
  }, [employeeNotice]);
  // Document version pending permanent deletion: { uid, groupId, label, fileCount }.
  const [deleteDocTarget, setDeleteDocTarget] = useState(null);
  const [deletingDocVersion, setDeletingDocVersion] = useState(false);
  const [deleteDocError, setDeleteDocError] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const docTypesLoaded = useRef(false);

  const [clientInvoices, setClientInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invError, setInvError] = useState(null);
  const [invPage, setInvPage] = useState(0);
  const [invRowsPerPage, setInvRowsPerPage] = useState(20);
  const [invTotal, setInvTotal] = useState(0);
  const invFetchKey = useRef(null);

  const [workOrders, setWorkOrders] = useState([]);
  const [woLoading, setWoLoading] = useState(false);
  const [woError, setWoError] = useState(null);
  const [woPage, setWoPage] = useState(0);
  const [woRowsPerPage, setWoRowsPerPage] = useState(20);
  const [woTotal, setWoTotal] = useState(0);
  const woFetchKey = useRef(null);

  const formatCurrency = (amount) => `AED ${(parseFloat(amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatMoney = (amount) => (amount == null ? '—' : formatCurrency(amount));
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');
  const prettyType = (t) => (t || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Cross-feature reads: only call what this user can read (outstanding + wallet = finance,
        // org summary = invoice); the rest resolve empty so the page renders without 403 noise.
        const empty = (data) => Promise.resolve({ data: { data } });
        const [invoicesRes, walletRes, summaryRes] = await Promise.allSettled([
          canReadFinance ? ApiService.getOutstandingInvoicesForOrg(orgId) : empty([]),
          canReadFinance ? ApiService.getCustomerWalletByOrg(orgId) : empty(null),
          canReadInvoice ? ApiService.getInvoiceOrgSummary([orgId]) : empty([])
        ]);

        if (invoicesRes.status === 'fulfilled') {
          const data = invoicesRes.value?.data?.data || invoicesRes.value?.data || [];
          setInvoices(Array.isArray(data) ? data : []);
        }

        if (walletRes.status === 'fulfilled') {
          const data = walletRes.value?.data?.data || walletRes.value?.data || {};
          setWallet(data);
        }
        setWalletOk(canReadFinance && walletRes.status === 'fulfilled');

        if (summaryRes.status === 'fulfilled') {
          const rows = summaryRes.value?.data?.data || [];
          setSummary(Array.isArray(rows) ? (rows[0] || null) : null);
        }
        setSummaryOk(canReadInvoice && summaryRes.status === 'fulfilled');
      } catch (e) {
        console.error('Error fetching org detail', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId, canReadFinance, canReadInvoice]);

  const loadEmployees = useCallback(async (page, size) => {
    setEmployeesLoading(true);
    setEmployeesError(null);
    try {
      const resp = await ApiService.getClientEmployees(orgId, page, size, statusFilterList(empStatusFilter));
      const payload = resp?.data?.data || resp?.data || {};
      // Paginated envelope { content, totalElements, ... }; tolerate a bare array too.
      const list = Array.isArray(payload) ? payload : (payload.content || []);
      setEmployees(list);
      setEmpTotal(Array.isArray(payload) ? list.length : (payload.totalElements ?? list.length));
      setExpandedUid(null);
    } catch (e) {
      setEmployeesError(e?.response?.data?.error || e?.response?.data?.message || 'Failed to load employees');
    } finally {
      setEmployeesLoading(false);
    }
  }, [orgId, empStatusFilter]);

  // Load employees the first time the tab is opened, and whenever the page/size/filter changes.
  useEffect(() => {
    if (tab === 'employees' && showEmployeesTab) loadEmployees(empPage, empRowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, empPage, empRowsPerPage, empStatusFilter]);

  const handleStatusUpdated = (updated) => {
    setStatusTarget(null);
    setEmployeeNotice({ severity: 'success', text: `Status changed to ${prettyType(updated.status)}` });
    const allowed = statusFilterList(empStatusFilter);
    if (allowed && !allowed.includes(updated.status)) {
      loadEmployees(empPage, empRowsPerPage);
      return;
    }
    setEmployees((prev) => prev.map((e) => (e.uid === updated.uid ? { ...e, ...updated } : e)));
  };

  // Document types for uploads: the TC's own catalogue first, the Thaklis list as fallback.
  useEffect(() => {
    if (tab !== 'employees' || !showEmployeesTab || !canWriteOrg || docTypesLoaded.current) return;
    docTypesLoaded.current = true;
    (async () => {
      try {
        const tcTypes = await tcDocumentTypeService.getActive('EMPLOYEE').catch(() => []);
        if (tcTypes.length > 0) {
          setDocumentTypes(tcTypes.map((dt) => ({ code: dt.code, label: dt.label, category: dt.category })));
          return;
        }
        const resp = await ApiService.getDocumentTypes();
        setDocumentTypes(resp?.data?.data || []);
      } catch (e) {
        setDocumentTypes([]);
      }
    })();
  }, [tab, showEmployeesTab, canWriteOrg]);

  const loadEmployeeDocs = useCallback(async (uid) => {
    setDocsByUid((prev) => ({ ...prev, [uid]: { loading: true, error: null, groups: [] } }));
    try {
      const resp = await ApiService.getClientEmployeeDocuments(orgId, uid);
      const payload = resp?.data?.data || resp?.data || {};
      const groups = Array.isArray(payload?.groups) ? payload.groups : [];
      setDocsByUid((prev) => ({ ...prev, [uid]: { loading: false, error: null, groups } }));
    } catch (e) {
      setDocsByUid((prev) => ({
        ...prev,
        [uid]: { loading: false, error: e?.response?.data?.error || 'Failed to load documents', groups: [] }
      }));
    }
  }, [orgId]);

  const handleEmployeeCreated = async (result) => {
    setAddEmployeeOpen(false);
    setEmployeeNotice({
      severity: result.passportUploadError ? 'warning' : 'success',
      text: result.passportUploadError
        || (result.reused
          ? 'An employee with this passport or Emirates ID already exists in Thaklis and was reused. If it was created outside this client it will not appear in this list.'
          : 'Employee added.')
    });
    setDocsByUid((prev) => { const next = { ...prev }; delete next[result.uid]; return next; });
    if (empPage !== 0) setEmpPage(0); else await loadEmployees(0, empRowsPerPage);
    setExpandedUid(result.uid);
    loadEmployeeDocs(result.uid);
  };

  const handleDocumentsUploaded = (uid, result) => {
    setUploadTarget(null);
    setEmployeeNotice({ severity: 'success', text: `${result.count || 1} document(s) uploaded.` });
    setExpandedUid(uid);
    loadEmployeeDocs(uid);
  };

  const loadClientInvoices = useCallback(async (page, size) => {
    setInvLoading(true);
    setInvError(null);
    if (!canReadInvoice) {
      setClientInvoices([]);
      setInvTotal(0);
      setInvLoading(false);
      return;
    }
    try {
      const resp = await ApiService.getInvoices({
        internalOrganizationId: orgId,
        limit: size,
        offset: page * size
      });
      const payload = resp?.data || {};
      const list = Array.isArray(payload.data) ? payload.data : [];
      setClientInvoices(list);
      setInvTotal(payload.total ?? list.length);
    } catch (e) {
      setInvError(e?.response?.data?.error || e?.response?.data?.message || 'Failed to load invoices');
    } finally {
      setInvLoading(false);
    }
  }, [orgId, canReadInvoice]);

  const loadWorkOrders = useCallback(async (page, size) => {
    setWoLoading(true);
    setWoError(null);
    if (!canReadWorkOrder) {
      setWorkOrders([]);
      setWoTotal(0);
      setWoLoading(false);
      return;
    }
    try {
      const resp = await ApiService.getWorkOrders({
        organizationId: orgId,
        limit: size,
        offset: page * size
      });
      const payload = resp?.data || {};
      const list = Array.isArray(payload.data) ? payload.data : [];
      setWorkOrders(list);
      setWoTotal(payload.total ?? list.length);
    } catch (e) {
      setWoError(e?.response?.data?.error || e?.response?.data?.message || 'Failed to load work orders');
    } finally {
      setWoLoading(false);
    }
  }, [orgId, canReadWorkOrder]);

  // Fetch on first open and on page/size change, but not when toggling back to a loaded tab.
  useEffect(() => {
    if (tab !== 'invoices') return;
    const key = `${invPage}|${invRowsPerPage}`;
    if (invFetchKey.current === key) return;
    invFetchKey.current = key;
    loadClientInvoices(invPage, invRowsPerPage);
  }, [tab, invPage, invRowsPerPage, loadClientInvoices]);

  useEffect(() => {
    if (tab !== 'workOrders') return;
    const key = `${woPage}|${woRowsPerPage}`;
    if (woFetchKey.current === key) return;
    woFetchKey.current = key;
    loadWorkOrders(woPage, woRowsPerPage);
  }, [tab, woPage, woRowsPerPage, loadWorkOrders]);

  const toggleEmployee = async (uid) => {
    if (!uid) return;
    if (expandedUid === uid) {
      setExpandedUid(null);
      return;
    }
    setExpandedUid(uid);
    if (!docsByUid[uid]) await loadEmployeeDocs(uid);
  };

  // Fetch the document through the authenticated client (JWT header) as a blob, then open it in a new
  // tab. A plain link/window.open can't carry the auth header, so we stream + object-URL it.
  const handleViewDoc = async (filePath, docKey) => {
    if (!filePath) return;
    setViewingDoc(docKey);
    try {
      const resp = await ApiService.viewClientEmployeeDocument(orgId, filePath);
      const blob = new Blob([resp.data], { type: resp.data?.type || resp.headers?.['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // Revoke a little later so the new tab has time to load it.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setEmployeesError('Failed to open document');
    } finally {
      setViewingDoc(null);
    }
  };

  // Permanently delete one version (all its files) — Thaklis renumbers later versions and restores
  // the newest remaining one as current, so the documents are reloaded afterwards.
  const handleConfirmDeleteDocVersion = async () => {
    if (!deleteDocTarget) return;
    setDeletingDocVersion(true);
    setDeleteDocError(null);
    try {
      await ApiService.deleteClientEmployeeDocumentVersion(orgId, deleteDocTarget.uid, deleteDocTarget.groupId);
      setEmployeeNotice({ severity: 'success', text: `${deleteDocTarget.label} deleted permanently` });
      const uid = deleteDocTarget.uid;
      setDeleteDocTarget(null);
      await loadEmployeeDocs(uid);
    } catch (e) {
      setDeleteDocError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to delete the document');
    } finally {
      setDeletingDocVersion(false);
    }
  };

  const invoiced = summaryOk ? Number(summary?.invoiced_amount ?? summary?.invoicedAmount ?? 0) : null;
  const collected = summaryOk ? Number(summary?.collected_amount ?? summary?.collectedAmount ?? 0) : null;
  const outstanding = summaryOk ? Number(summary?.outstanding_amount ?? summary?.outstandingAmount ?? 0) : null;
  const finalCount = Number(summary?.final_count ?? summary?.finalCount ?? 0);
  const draftCount = Number(summary?.draft_count ?? summary?.draftCount ?? 0);
  const walletBalance = walletOk ? Number(wallet?.approved_balance ?? wallet?.approvedBalance ?? 0) : null;
  const netLiability = outstanding == null || walletBalance == null ? null : outstanding - walletBalance;

  const renderOverviewTab = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }
    return (
      <Grid container spacing={3}>
        {/* Financial Summary Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <FinancialSummaryCard
            title="Revenue"
            accentColor="#73BF69"
            rows={[
              { label: 'Total Invoiced', value: formatMoney(invoiced) },
              { label: 'Collected', value: formatMoney(collected), color: '#86efac' },
              { label: 'Outstanding', value: formatMoney(outstanding), color: '#FF9830' },
            ]}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <FinancialSummaryCard
            title="Wallet"
            accentColor="#5794F2"
            rows={[
              { label: 'Balance', value: formatMoney(walletBalance), bold: true },
              { label: 'Net Liability', value: formatMoney(netLiability), color: netLiability == null ? undefined : netLiability > 0 ? '#fca5a5' : '#86efac' },
            ]}
            footer={{
              label: 'Invoices',
              value: summaryOk ? `${finalCount} Final | ${draftCount} Draft` : '—'
            }}
          />
        </Grid>

        {/* Contact Info Card */}
        <Grid item xs={12} sm={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  Contact Information
                </Typography>
                {canWriteOrg && (
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => navigate('/organizations')}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {[
                { icon: <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />, label: 'Email', value: org?.email },
                { icon: <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />, label: 'Contact', value: org?.contactPerson },
                { icon: <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />, label: 'Phone', value: org?.contactNumber },
                { icon: <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />, label: 'Address', value: org?.address },
              ].filter(r => r.value).map((row) => (
                <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {row.icon}
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', minWidth: 60 }}>
                    {row.label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                    {row.value}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Invoices Table */}
        {canReadFinance && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                  Outstanding Invoices ({invoices.length})
                </Typography>
                {invoices.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
                    No outstanding invoices for this organization
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Cost</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Markup</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Payment</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoices.map((inv) => {
                          const total = parseFloat(inv.grand_total || inv.grandTotal || 0);
                          const cost = parseFloat(inv.internal_total || inv.internalTotal || 0);
                          const markup = total - cost;
                          const invNumber = inv.invoice_number || inv.invoiceNumber || '-';
                          const invDate = inv.invoice_date || inv.invoiceDate;
                          const itemCount = inv.item_count || inv.lineItemCount || inv.items || 0;
                          const paymentStatus = inv.customer_payment_status || inv.paymentStatus || 'Unpaid';
                          return (
                            <TableRow key={inv.id} hover>
                              <TableCell>
                                {canReadInvoice ? (
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer' }}
                                    onClick={() => navigate(`/invoices/${inv.id}`)}>
                                    {invNumber}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{invNumber}</Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }}>
                                {invDate ? new Date(invDate).toLocaleDateString() : '-'}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }}>{itemCount}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{formatCurrency(total)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatCurrency(cost)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.8rem', color: markup > 0 ? '#FF9830' : undefined }}>
                                {formatCurrency(markup)}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={inv.status || '-'}
                                  size="small"
                                  color={inv.status === 'FINAL' ? 'success' : 'default'}
                                  sx={{ fontSize: '0.7rem', height: 22 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={paymentStatus}
                                  size="small"
                                  color={paymentStatus === 'PAID' ? 'success' : 'warning'}
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 22 }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderEmployeesTab = () => {
    if (employeesLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      );
    }
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Employees ({empTotal})
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TextField
                select
                size="small"
                label="Status"
                value={empStatusFilter}
                onChange={(e) => { setEmpStatusFilter(e.target.value); setEmpPage(0); }}
                sx={{ minWidth: 200 }}
              >
                {EMPLOYEE_STATUS_FILTERS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
              </TextField>
              {canWriteOrg && (
                <Button size="small" variant="contained" startIcon={<PersonAddIcon />} onClick={() => setAddEmployeeOpen(true)}>
                  Add Employee
                </Button>
              )}
            </Box>
          </Box>

          {employeesError && <Alert severity="error" sx={{ mb: 2 }}>{employeesError}</Alert>}
          {employeeNotice && (
            <Alert severity={employeeNotice.severity} sx={{ mb: 2 }} onClose={() => setEmployeeNotice(null)}>
              {employeeNotice.text}
            </Alert>
          )}

          {employees.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
              {empStatusFilter === 'ALL' ? 'No employees tracked for this client yet.' : 'No employees match this status filter.'}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Passport</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Emirates ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    {canWriteOrg && <TableCell sx={{ width: 96 }} />}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((emp) => {
                    const uid = emp.uid;
                    const name = emp.name || [emp.firstName, emp.lastName].filter(Boolean).join(' ') || '-';
                    const expanded = expandedUid === uid;
                    const docState = docsByUid[uid];
                    const statusMeta = EMPLOYEE_STATUSES.find((s) => s.value === emp.status);
                    return (
                      <React.Fragment key={uid || emp.employeeId}>
                        <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleEmployee(uid)}>
                          <TableCell>
                            <IconButton size="small">
                              {expanded ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
                          <TableCell>
                            {emp.status ? (
                              <Tooltip title={emp.statusInfo || ''}>
                                <Chip
                                  size="small"
                                  label={statusMeta?.label || prettyType(emp.status)}
                                  color={statusMeta?.color || 'default'}
                                  sx={{ fontSize: '0.7rem', height: 22 }}
                                />
                              </Tooltip>
                            ) : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{emp.passportNumber || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{emp.emiratesId || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{emp.mobileNumber || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{emp.email || '-'}</TableCell>
                          {canWriteOrg && (
                            <TableCell sx={{ py: 0, whiteSpace: 'nowrap' }}>
                              <Tooltip title="Change status">
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); setStatusTarget({ ...emp, name }); }}
                                >
                                  <StatusIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Upload documents">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={(e) => { e.stopPropagation(); setUploadTarget({ uid, name }); }}
                                >
                                  <UploadFileIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ p: 0, border: 0 }} colSpan={canWriteOrg ? 8 : 7}>
                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                                {docState?.loading ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={22} />
                                  </Box>
                                ) : docState?.error ? (
                                  <Alert severity="error">{docState.error}</Alert>
                                ) : (docState?.groups || []).length === 0 ? (
                                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    No documents uploaded for this employee.
                                  </Typography>
                                ) : (
                                  <EmployeeDocuments
                                    uid={uid}
                                    groups={docState.groups}
                                    onView={handleViewDoc}
                                    viewingKey={viewingDoc}
                                    onDelete={canDeleteOrg ? (target) => { setDeleteDocError(null); setDeleteDocTarget(target); } : undefined}
                                  />
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {empTotal > 0 && (
            <TablePagination
              component="div"
              count={empTotal}
              page={empPage}
              rowsPerPage={empRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
              onPageChange={(e, newPage) => setEmpPage(newPage)}
              onRowsPerPageChange={(e) => { setEmpRowsPerPage(parseInt(e.target.value, 10)); setEmpPage(0); }}
            />
          )}
        </CardContent>

        {canWriteOrg && (
          <>
            <AddClientEmployeeDialog
              open={addEmployeeOpen}
              orgId={orgId}
              onClose={() => setAddEmployeeOpen(false)}
              onCreated={handleEmployeeCreated}
            />
            <UploadClientEmployeeDocumentsDialog
              open={!!uploadTarget}
              orgId={orgId}
              uid={uploadTarget?.uid}
              employeeName={uploadTarget?.name}
              documentTypes={documentTypes}
              onClose={() => setUploadTarget(null)}
              onUploaded={(result) => handleDocumentsUploaded(uploadTarget?.uid, result)}
            />
            <ChangeEmployeeStatusDialog
              open={!!statusTarget}
              orgId={orgId}
              employee={statusTarget}
              onClose={() => setStatusTarget(null)}
              onUpdated={handleStatusUpdated}
            />
          </>
        )}

        <Dialog
          open={!!deleteDocTarget}
          onClose={() => !deletingDocVersion && setDeleteDocTarget(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: '0.95rem' }}>Delete document permanently?</DialogTitle>
          <DialogContent>
            {deleteDocError && <Alert severity="error" sx={{ mb: 1.5 }}>{deleteDocError}</Alert>}
            <Typography variant="body2">
              {deleteDocTarget?.label}
              {deleteDocTarget?.fileCount > 1 ? ` (${deleteDocTarget.fileCount} files)` : ''} will be deleted
              permanently, including the stored file{deleteDocTarget?.fileCount > 1 ? 's' : ''}. This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDocTarget(null)} disabled={deletingDocVersion}>Cancel</Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleConfirmDeleteDocVersion}
              disabled={deletingDocVersion}
              startIcon={deletingDocVersion ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
            >
              {deletingDocVersion ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    );
  };

  const renderInvoicesTab = () => {
    if (invLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      );
    }
    return (
      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            Invoices ({invTotal})
          </Typography>

          {invError && <Alert severity="error" sx={{ mb: 2 }}>{invError}</Alert>}

          {clientInvoices.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
              No invoices raised for this organization yet.
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Invoice #</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Cost</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Markup</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Payment</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientInvoices.map((inv) => {
                      const total = parseFloat(inv.grand_total || inv.grandTotal || 0);
                      const cost = parseFloat(inv.internal_total || inv.internalTotal || 0);
                      const markup = total - cost;
                      const invNumber = inv.invoice_number || inv.invoiceNumber || '-';
                      const invDate = inv.invoice_date || inv.invoiceDate;
                      const itemCount = inv.item_count || inv.lineItemCount
                        || (Array.isArray(inv.line_items) ? inv.line_items.length : 0)
                        || (Array.isArray(inv.lineItems) ? inv.lineItems.length : 0);
                      const paymentStatus = inv.customer_payment_status || inv.paymentStatus || 'Unpaid';
                      return (
                        <TableRow key={inv.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer' }}
                              onClick={() => navigate(`/invoices/${inv.id}`)}>
                              {invNumber}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(invDate)}</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{itemCount}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{formatCurrency(total)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatCurrency(cost)}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', color: markup > 0 ? '#FF9830' : undefined }}>
                            {formatCurrency(markup)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={inv.status || '-'}
                              size="small"
                              color={inv.status === 'FINAL' ? 'success' : 'default'}
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={paymentStatus}
                              size="small"
                              color={paymentStatus === 'PAID' ? 'success' : 'warning'}
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={invTotal}
                page={invPage}
                rowsPerPage={invRowsPerPage}
                rowsPerPageOptions={[10, 20, 50, 100]}
                onPageChange={(e, newPage) => setInvPage(newPage)}
                onRowsPerPageChange={(e) => { setInvRowsPerPage(parseInt(e.target.value, 10)); setInvPage(0); }}
              />
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderWorkOrdersTab = () => {
    if (woLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      );
    }
    const statusColor = {
      OPEN: 'info', IN_PROGRESS: 'warning', ON_HOLD: 'warning',
      COMPLETED: 'success', CANCELLED: 'error', INVOICED: 'secondary', PARTIALLY_INVOICED: 'info'
    };
    return (
      <Card>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
            Work Orders ({woTotal})
          </Typography>

          {woError && <Alert severity="error" sx={{ mb: 2 }}>{woError}</Alert>}

          {workOrders.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 3 }}>
              No work orders raised for this organization yet.
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Work Order #</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Progress</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workOrders.map((wo) => {
                      const woNumber = wo.work_order_number || wo.workOrderNumber || '-';
                      const woDate = wo.order_date || wo.orderDate;
                      const done = wo.completed_tasks ?? wo.completedTasks ?? 0;
                      const totalTasks = wo.total_tasks ?? wo.totalTasks ?? 0;
                      const percent = wo.progress_percent ?? wo.progressPercent ?? 0;
                      const woStatus = wo.status || '-';
                      return (
                        <TableRow key={wo.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer' }}
                              onClick={() => navigate(`/work-orders/${wo.id}`)}>
                              {woNumber}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>{formatDate(woDate)}</TableCell>
                          <TableCell>
                            <Box sx={{ width: 100 }}>
                              <Typography variant="caption">{done}/{totalTasks} tasks</Typography>
                              <LinearProgress
                                variant="determinate"
                                value={percent}
                                sx={{ height: 6, borderRadius: 1 }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            {formatCurrency(wo.grand_total || wo.grandTotal)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={prettyType(woStatus)}
                              size="small"
                              color={statusColor[woStatus] || 'default'}
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={woTotal}
                page={woPage}
                rowsPerPage={woRowsPerPage}
                rowsPerPageOptions={[10, 20, 50, 100]}
                onPageChange={(e, newPage) => setWoPage(newPage)}
                onRowsPerPageChange={(e) => { setWoRowsPerPage(parseInt(e.target.value, 10)); setWoPage(0); }}
              />
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  const tabs = [
    { key: 'overview', label: 'Overview', render: renderOverviewTab },
    // Employees/documents are tracked only for client-type customers, not typing-center customers.
    ...(showEmployeesTab ? [{ key: 'employees', label: 'Employees', render: renderEmployeesTab }] : []),
    ...(showEmployeesTab ? [{
      key: 'documents',
      label: 'Documents',
      render: () => <OrgDocumentsTab orgId={orgId} canWrite={canWriteOrg} canDelete={canDeleteOrg} />
    }] : []),
    ...(canReadInvoice ? [{ key: 'invoices', label: 'Invoices', render: renderInvoicesTab }] : []),
    ...(canReadWorkOrder ? [{ key: 'workOrders', label: 'Work Orders', render: renderWorkOrdersTab }] : []),
    // Saved cards (metadata only) picked when a government fee is paid by the customer's card. Listing needs work order read.
    // Cards are managed with finance; work order users still see them (the same list fills their payment dropdowns)
    ...(canReadWorkOrder || canReadFinance ? [{ key: 'cards', label: 'Saved Cards', render: () => <ClientCardsTab orgId={orgId} canWrite={canWriteFinance} canRemove={canDeleteFinance} canSeePayments={canReadFinance} /> }] : []),
    // Portal access (feature switches) + portal users for this client.
    {
      key: 'portal',
      label: 'Portal',
      render: () => (
        <ClientPortalTab
          org={org}
          orgId={orgId}
          canWrite={canWriteOrg}
          showThaklisFeatures={showEmployeesTab && sessionUser?.thaklisEnabled !== false}
        />
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={() => navigate('/organizations')}>
          <ArrowBackIcon />
        </IconButton>
        <BusinessIcon sx={{ color: 'secondary.main', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {org?.name || 'Organization'}
          </Typography>
          <Chip label="Client" size="small" sx={{ mt: 0.5 }} />
        </Box>
      </Box>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        {tabs.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} sx={{ textTransform: 'none', fontWeight: 600 }} />
        ))}
      </Tabs>

      {tabs.find((t) => t.key === tab)?.render()}
    </Box>
  );
};

export default InternalOrgDetail;
